import { useEffect, useState } from "react";
import { Eye, EyeOff, KeyRound, Loader2, Megaphone, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui";
import { Entrada, Campo } from "../components/Entrada";
import { auth, type ServidorInfo } from "../lib/auth";
import { VERSAO } from "../lib/versao";

/**
 * Entrada.
 *
 * Três caminhos, na ordem em que o Traccar os oferece: senha (com segundo
 * fator quando o usuário tem 2FA), identidade única (OpenID) e token — o link
 * temporário que alguém recebeu para acompanhar um veículo.
 *
 * A mensagem de falha é a MESMA para usuário inexistente e senha errada.
 * Distinguir os dois deixa qualquer um descobrir quem tem conta no painel.
 */
export default function Login({ nome, logo, onEntrar, aoRecuperar, aoRegistrar }: {
  nome: string;
  logo?: string | null;
  onEntrar: () => void;
  aoRecuperar: () => void;
  aoRegistrar: () => void;
}) {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [verSenha, setVerSenha] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [pedeCodigo, setPedeCodigo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [indo, setIndo] = useState(false);
  const [srv, setSrv] = useState<ServidorInfo | null>(null);

  useEffect(() => { auth.servidor().then(setSrv).catch(() => setSrv(null)); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setIndo(true); setErro(null);
    const r = await auth.entrar(email, senha, pedeCodigo ? codigo : undefined);
    setIndo(false);
    if (r.estado === "ok") { onEntrar(); return; }
    if (r.estado === "2fa") { setPedeCodigo(true); setErro(null); return; }
    setErro(r.motivo);
    setSenha("");
  }

  const soOpenId = srv?.openIdEnabled && srv.openIdForce;

  return (
    <Entrada marca={nome} logo={logo}>
      <h1 className="text-3xl font-semibold text-ink-800 dark:text-white/90">Entrar</h1>
      <p className="mt-2 text-sm text-ink-500 dark:text-ink-400">
        Informe seu usuário e senha para acompanhar a frota.
      </p>

      {srv?.announcement && (
        <p className="mt-5 flex items-start gap-2 rounded-lg border border-[var(--color-warning)]/40
                      bg-[var(--color-warning)]/10 px-3.5 py-3 text-sm text-ink-700 dark:text-ink-200">
          <Megaphone size={16} className="mt-0.5 shrink-0 text-[var(--color-warning)]" />
          {srv.announcement}
        </p>
      )}

      {srv?.openIdEnabled && (
        <>
          <button type="button" onClick={auth.entrarComOpenId}
            className="mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-lg border
                       border-ink-200 bg-ink-50 px-4 text-sm font-medium text-ink-700 t-fast
                       hover:bg-ink-100 dark:border-ink-800 dark:bg-white/[0.03] dark:text-ink-200">
            <KeyRound size={16} /> Entrar com a conta da empresa
          </button>
          {!soOpenId && (
            <div className="my-5 flex items-center gap-4">
              <span className="h-px flex-1 bg-ink-200 dark:bg-ink-800" />
              <span className="text-sm text-ink-400">ou</span>
              <span className="h-px flex-1 bg-ink-200 dark:bg-ink-800" />
            </div>
          )}
        </>
      )}

      {!soOpenId && (
        <form onSubmit={submit} className="mt-6 space-y-4">
          {/* ⚠️ `type="text"`, não `type="email"`. O campo é o USUÁRIO: no
              Traccar a coluna chama-se `email`, mas o que vale é o que a
              pessoa digita para entrar, e nada obriga a ser um endereço. Com
              `type="email"` o navegador recusava o formulário antes de ele
              sair — "inclua um @ no endereço" — e o usuário legítimo não
              conseguia entrar de jeito nenhum. Validar aqui é decidir pelo
              cliente qual é o nome de quem ele cadastrou. */}
          <Campo label="Usuário" type="text" value={email} onChange={setEmail} obrigatorio
                 placeholder="Seu usuário" autoFocus autoComplete="username"
                 disabled={pedeCodigo} />

          <div className="relative">
            <Campo label="Senha" type={verSenha ? "text" : "password"} value={senha}
                   onChange={setSenha} obrigatorio placeholder="Sua senha"
                   autoComplete="current-password" disabled={pedeCodigo} className="campo pr-11" />
            <button type="button" onClick={() => setVerSenha(v => !v)}
              aria-label={verSenha ? "Ocultar senha" : "Mostrar senha"}
              className="absolute right-3.5 top-[38px] text-ink-400 t-fast hover:text-ink-600">
              {verSenha ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>

          {pedeCodigo && (
            <div className="rounded-lg border border-ink-200 bg-ink-50 p-4 dark:border-ink-800
                            dark:bg-white/[0.03]">
              <p className="mb-3 flex items-center gap-2 text-sm text-ink-600 dark:text-ink-300">
                <ShieldCheck size={16} style={{ color: "var(--color-brand-tenant)" }} />
                Esta conta usa verificação em duas etapas.
              </p>
              <Campo label="Código do aplicativo" value={codigo} onChange={setCodigo}
                     autoFocus inputMode="numeric" autoComplete="one-time-code"
                     maxLength={6} placeholder="000000" />
            </div>
          )}

          {erro && (
            <p role="alert" className="rounded-lg bg-[var(--color-critical)]/10 px-3.5 py-3 text-sm"
               style={{ color: "var(--color-critical)" }}>
              {erro}
            </p>
          )}

          <div className="flex items-center justify-between">
            <button type="button" onClick={aoRecuperar}
              className="text-sm text-[var(--color-brand-text)] t-fast hover:underline">
              Esqueci minha senha
            </button>
            {srv?.registration && (
              <button type="button" onClick={aoRegistrar}
                className="text-sm text-ink-500 t-fast hover:text-ink-700">
                Criar conta
              </button>
            )}
          </div>

          <Button type="submit" className="w-full"
                  disabled={indo || !email || !senha || (pedeCodigo && codigo.length < 6)}>
            {indo && <Loader2 size={16} className="animate-spin" />}
            {indo ? "Entrando…" : pedeCodigo ? "Confirmar código" : "Entrar"}
          </Button>
        </form>
      )}

      {/* A versão publicada, abaixo do botão. Fica visível sem entrar no
          sistema, de propósito: é assim que se confere, olhando, se um cliente
          ficou atrás numa atualização. Discreta — informação de conferência,
          não de uso. */}
      <p className="mt-6 text-center text-xs text-ink-400 dark:text-ink-500"
         title={`Versão publicada: ${VERSAO}`}>
        versão {VERSAO}
      </p>
    </Entrada>
  );
}

export { Campo };
